import React from 'react';
import { MenuItem } from '@mui/material';

// components
import GridPageAbstract from '../abstract/GridPageAbstract';
import CreateTransactionDialog from '../dialogs/CreateTransactionDialog';
import Iconify from '../../components/iconify';
import { mergeHeadWithStorage } from '../../utils/gridHeadStorage';
import i18n from '../../i18n';

// Must match action-permissions.yml in x-backend and the constants in
// co.itfed.spring.api.utils.ActionPermissions exactly. Payin and payout are
// gated separately: a payout moves money out, which is not the same decision as
// letting someone raise a test payment.
const PAYIN_ACTION = 'merchant-terminal-payin';
const PAYOUT_ACTION = 'merchant-terminal-payout';

export default class MerchantTerminalsPage extends GridPageAbstract {
    constructor(props) {
        super(props);
        this.Name = 'publicMerchantTerminals';
        this.Server = this.props.context.getState().server.publicMerchantTerminals;
        this.MenuDisplayType = 'menu';
        this.searchFields = ['name'];

        const defaultHead = [
            { id: 'uuid', label: 'uuid', alignRight: false, cell: 'Text', displayGrid: true, disableEditor: true },
            { id: 'name', required: true, label: 'name', alignRight: false, cell: 'Text', displayGrid: true },
            {
                id: 'merchantId', required: true,
                label: 'merchant',
                alignRight: false,
                cell: 'Select',
                from: 'publicMerchants',
                object: 'merchant',
                show: 'name',
                displayGrid: true
            },
            {
                id: 'payinGatewayId',
                label: 'payinGateway',
                alignRight: false,
                cell: 'Select',
                from: 'publicGateways',
                object: 'payinGateway',
                show: 'name',
                displayGrid: true
            },
            {
                id: 'payoutGatewayId',
                label: 'payoutGateway',
                alignRight: false,
                cell: 'Select',
                from: 'publicGateways',
                object: 'payoutGateway',
                show: 'name',
                displayGrid: true
            },
            {
                id: 'currencyId', required: true,
                label: 'currency',
                alignRight: false,
                cell: 'Select',
                from: 'publicCurrencies',
                object: 'currency',
                show: 'code',
                displayGrid: true
            },
            // Empty means the same as the currency. Must match the balance currency
            // of its payin and payout gateways, and of any gateway a wallet pins this
            // terminal to, wherever they name one (TRIGGER_BALANCE_CURRENCY_MISMATCH).
            {
                id: 'balanceCurrencyId',
                label: 'balanceCurrency',
                alignRight: false,
                cell: 'Select',
                from: 'publicCurrencies',
                object: 'balanceCurrency',
                show: 'code',
                displayGrid: true
            },
            { id: 'apiKey', label: 'apiKey', alignRight: false, cell: 'Text', displayGrid: true, disableEditor: true },
            // Generated server-side and never accepted from a request, so it has
            // no place in the add/edit form — it was a plain text box anyone could
            // overwrite. disableEditor keeps it out of both forms while leaving it
            // readable where it is needed.
            {
                id: 'signKey',
                label: 'signKey',
                alignRight: false,
                cell: 'Text',
                displayGrid: false,
                disableEditor: true
            },
            { id: 'merchantUrl', label: 'merchantUrl', alignRight: false, cell: 'Url', displayGrid: true, disableEditor: true },
            {
                id: 'ipWhitelist',
                label: 'ipWhitelist',
                alignRight: false,
                cell: 'Tags',
                displayGrid: true,
                disableEditor: true,
                details: 'Array'
            },
            { id: 'settlementPeriod', label: 'settlementPeriod', alignRight: true, cell: 'Number', displayGrid: true },
            { id: 'isTrusted', label: 'isTrusted', alignRight: false, cell: 'Boolean', displayGrid: true },
            { id: 'isArchived', label: 'isArchived', alignRight: false, cell: 'Boolean', displayGrid: true },
            {
                id: 'modifiedReason',
                label: 'modifiedReason',
                alignRight: false,
                cell: 'BigText',
                displayGrid: true,
                disableEditor: true
            },
            {
                id: 'modifiedBy',
                label: 'modifiedBy',
                alignRight: false,
                cell: 'Select',
                object: 'modifiedBy',
                show: 'firstName+lastName',
                displayGrid: true,
                disableEditor: true,
                customSort: 'modifiedBy.firstName'
            },
            {
                id: 'dateCreatedModified',
                elements: ['dateCreated', 'dateModified'],
                label: 'dateCreatedModified',
                alignRight: false,
                cell: 'CreateModify',
                disableEditor: true,
                displayGrid: true,
                customSort: 'dateCreated'
            }
        ];
        this.Head = mergeHeadWithStorage(this.Name, defaultHead);

        this.state = {
            ...this.state,
            head: this.Head,
            transactionType: null,
            transactionTerminal: null
        };
    }

    /**
     * Fields for the create-payment dialog, mirroring the payment core's
     * PayinCreateRequest.
     *
     * `Amount` rather than `Number` for the amount: AmountInput displays major
     * units and emits minor ones, so the operator types 15.00 and 1500 goes on the
     * wire — which is what the core wants and what nobody would guess from a box
     * labelled "amount".
     */
    payinHead(terminal) {
        return [
            { id: 'currency', label: 'currency', cell: 'Text', default: terminal?.currency?.code || '' },
            { id: 'amount', label: 'amount', cell: 'Amount', required: true },
            { id: 'p2pMethod', label: 'p2pMethod', cell: 'Text', required: true, helperText: 'p2pMethod' },
            { id: 'description', label: 'description', cell: 'Text', required: true },
            { id: 'userName', label: 'userName', cell: 'Text', required: true },
            { id: 'email', label: 'email', cell: 'Text', required: true },
            { id: 'phone', label: 'phone', cell: 'Text', required: true },
            { id: 'country', label: 'country', cell: 'Text', helperText: 'countryAlpha2' },
            { id: 'merchantTransactionId', label: 'merchantTransactionId', cell: 'Text' },
            { id: 'clientIp', label: 'clientIp', cell: 'Text' },
            { id: 'callbackUrl', label: 'callbackUrl', cell: 'Text' }
        ];
    }

    /**
     * Fields for the create-payout dialog.
     *
     * `currency` is read-only here — the payout request has no currency field —
     * but it still earns its place: it sits in the form data, which is where
     * DialogAmountField looks for a currency symbol, and the dialog strips
     * onlyRead fields before posting.
     */
    payoutHead(terminal) {
        return [
            { id: 'currency', label: 'currency', cell: 'Text', onlyRead: true, default: terminal?.currency?.code || '' },
            { id: 'amount', label: 'amount', cell: 'Amount', required: true },
            { id: 'payeeName', label: 'payeeName', cell: 'Text', required: true },
            { id: 'email', label: 'email', cell: 'Text', required: true },
            { id: 'phone', label: 'phone', cell: 'Text', required: true },
            { id: 'description', label: 'description', cell: 'Text', required: true },
            { id: 'receiverInfo', label: 'receiverInfo', cell: 'BigText', required: true, json: true, helperText: 'receiverInfoJson' },
            { id: 'card', label: 'card', cell: 'Text' },
            { id: 'bank', label: 'bank', cell: 'Text' },
            { id: 'payoutMethod', label: 'payoutMethod', cell: 'Text' },
            { id: 'merchantTransactionId', label: 'merchantTransactionId', cell: 'Text' },
            { id: 'callbackUrl', label: 'callbackUrl', cell: 'Text' },
            { id: 'senderInfo', label: 'senderInfo', cell: 'BigText', json: true, helperText: 'senderInfoJson' }
        ];
    }

    openTransactionDialog(type) {
        // Read the row before closing the menu — handleCloseMenu nulls
        // menuClickItemId, so the other order loses the terminal.
        const terminal = this.getMenuClickItem();
        this.handleCloseMenu();
        this.setState({ transactionType: type, transactionTerminal: terminal });
    }

    closeTransactionDialog = () => {
        this.setState({ transactionType: null, transactionTerminal: null });
    };

    renderCustomMenuItems(id) {
        const row = this.List.find((item) => item.id === id);
        const items = [];

        // An array, not a fragment: hasMenuItems counts custom items with
        // Array.isArray(...) && length > 0, so a bare element does not register.
        if (this.allowedAction(PAYIN_ACTION) && row?.payinGateway) {
            items.push(
                <MenuItem key={`${id}-payin`} onClick={() => this.openTransactionDialog('payin')}>
                    <Iconify icon="eva:diagonal-arrow-left-down-fill" sx={{ mr: 2 }} />
                    {i18n.t('dialogs.createPayment')}
                </MenuItem>
            );
        }
        // Hidden when the terminal has no gateway for that direction: the core
        // would only answer GATEWAY_NOT_FOUND, and an action that cannot succeed
        // is worse than an absent one.
        if (this.allowedAction(PAYOUT_ACTION) && row?.payoutGateway) {
            items.push(
                <MenuItem key={`${id}-payout`} onClick={() => this.openTransactionDialog('payout')}>
                    <Iconify icon="eva:diagonal-arrow-right-up-fill" sx={{ mr: 2 }} />
                    {i18n.t('dialogs.createPayout')}
                </MenuItem>
            );
        }

        return items;
    }

    renderCustom() {
        const { transactionType, transactionTerminal } = this.state;
        if (!transactionType || !transactionTerminal) return null;

        const isPayin = transactionType === 'payin';

        return (
            <CreateTransactionDialog
                dialogOpen
                type={transactionType}
                id={transactionTerminal.id}
                Head={
                    isPayin
                        ? this.payinHead(transactionTerminal)
                        : this.payoutHead(transactionTerminal)
                }
                dialogTitle={isPayin ? 'dialogs.createPayment' : 'dialogs.createPayout'}
                // An arrow, not a bare method reference: the dialog calls
                // server(id, body) unbound, and the crud method needs its `this`.
                server={(id, body) =>
                    isPayin
                        ? this.Server.createPayin(id, body)
                        : this.Server.createPayout(id, body)
                }
                onClose={this.closeTransactionDialog}
            />
        );
    }
}
